export const VERIFIED_ADDRESS_STORAGE_EVENT = "verified-addresses:changed";
export const VERIFIED_ADDRESS_STORAGE_PREFIX = "metro-verified-addresses:v1:";

const VERSION = 1;
const MAX_ROWS = 20000;
const METRO_URL = "https://www.metrobyt-mobile.com/home-internet/check-eligibility";
const TMOBILE_URL = "https://www.t-mobile.com/home-internet/eligibility";
const FIBER_URL = "https://fiber.t-mobile.com/check-address";
const FIBER_RESULT_URL = "https://fiber.t-mobile.com/hint/eligibility";
const record = value => Boolean(value) && typeof value === "object" && !Array.isArray(value);
const text = (value, max = 250) => typeof value === "string" && Boolean(value.trim())
  // eslint-disable-next-line no-control-regex -- Reject ASCII control characters in locally stored address fields.
  && value.length <= max && !/[\u0000-\u001f\u007f]/.test(value);
const validTime = value => typeof value === "string" && Number.isFinite(Date.parse(value))
  && new Date(value).toISOString() === value && Date.parse(value) <= Date.now() + 300000;

export class VerifiedAddressStorageError extends Error {
  constructor(code, message, cause) {
    super(message);
    this.name = "VerifiedAddressStorageError";
    this.code = code;
    if (cause) this.cause = cause;
  }
}

const invalidRows = () => new VerifiedAddressStorageError("LOCAL_ADDRESSES_INVALID", "Only confirmed eligible addresses with a reference and verification time can be saved.");
const invalidNtid = () => new VerifiedAddressStorageError("LOCAL_NTID_INVALID", "Enter a valid NTID using 3-32 letters or numbers (for example, lkn01477).");
const corrupt = cause => new VerifiedAddressStorageError("LOCAL_ADDRESSES_CORRUPT", "The saved addresses in this browser could not be read.", cause);
const storageFailure = cause => new VerifiedAddressStorageError(
  cause?.name === "QuotaExceededError" || cause?.code === 22 ? "LOCAL_STORAGE_FULL" : "LOCAL_STORAGE_UNAVAILABLE",
  cause?.name === "QuotaExceededError" || cause?.code === 22
    ? "This browser's storage is full. Free some space before saving more addresses."
    : "This browser is blocking local address storage. Allow site storage and try again.", cause);

const ownerKey = userId => {
  if (!text(userId, 128)) throw new VerifiedAddressStorageError("LOCAL_AUTH_REQUIRED", "Sign in to use your saved addresses.");
  return `${VERIFIED_ADDRESS_STORAGE_PREFIX}${encodeURIComponent(userId)}`;
};

const getStorage = supplied => {
  try {
    const storage = supplied || (typeof window !== "undefined" ? window.localStorage : null);
    if (!storage || typeof storage.getItem !== "function" || typeof storage.setItem !== "function") throw new Error("Storage is unavailable.");
    return storage;
  } catch (error) { throw storageFailure(error); }
};

const parsedAddress = row => /^(\d[^,\r\n]*),\s*([^,\r\n]+),\s*(?:NV|NEVADA)\s+(\d{5})(?:-\d{4})?\s*$/i.exec(row.address);
const normalizeStreet = value => {
  let street = value.normalize("NFKC").toUpperCase();
  const unit = /\s+(?:(?:APARTMENT|APT\.?|UNIT|SUITE|STE\.?)\s+#?\s*|#\s*)([A-Z0-9][A-Z0-9 /-]*)$/.exec(street);
  // Normalize explicit unit identifiers, without turning a road called
  // "Apartment Circle" or "Unit Circle" into an apartment suffix.
  if (unit && (/\d/.test(unit[1]) || /^[A-Z]$/.test(unit[1]))) street = `${street.slice(0, unit.index)} UNIT ${unit[1]}`;
  return street.replace(/[^A-Z0-9/]+/g, " ").trim().replace(/\s+/g, " ")
    .replace(/\b(STREET|AVENUE|BOULEVARD|DRIVE|ROAD|LANE|COURT|CIRCLE|TRAIL|TERRACE|PLACE|PARKWAY|HIGHWAY|NORTH|SOUTH|EAST|WEST|NORTHEAST|NORTHWEST|SOUTHEAST|SOUTHWEST)\b/g,
      word => ({ STREET: "ST", AVENUE: "AVE", BOULEVARD: "BLVD", DRIVE: "DR", ROAD: "RD", LANE: "LN", COURT: "CT", CIRCLE: "CIR", TRAIL: "TRL", TERRACE: "TER", PLACE: "PL", PARKWAY: "PKWY", HIGHWAY: "HWY", NORTH: "N", SOUTH: "S", EAST: "E", WEST: "W", NORTHEAST: "NE", NORTHWEST: "NW", SOUTHEAST: "SE", SOUTHWEST: "SW" })[word]);
};
const physicalKey = row => `${row.zipCode}:${normalizeStreet(parsedAddress(row)[1])}`;

const sanitizeRow = row => {
  if (!record(row) || !["Metro", "T-Mobile"].includes(row.provider) || row.status !== "eligible"
    || !text(row.address) || !/^\d{5}$/.test(row.zipCode) || !text(row.referenceId)
    || row.id !== `${row.provider}:${row.referenceId}` || !validTime(row.checkedAt)) throw invalidRows();
  const address = parsedAddress(row);
  if (!address || address[3] !== row.zipCode) throw invalidRows();
  if (row.sourceUrl !== undefined && (row.provider === "Metro" ? row.sourceUrl !== METRO_URL : ![TMOBILE_URL, FIBER_URL].includes(row.sourceUrl))) throw invalidRows();
  if (row.resultUrl !== undefined && (row.provider === "Metro" ? row.resultUrl !== METRO_URL
    : row.sourceUrl === FIBER_URL ? row.resultUrl !== FIBER_RESULT_URL : row.resultUrl !== TMOBILE_URL)) throw invalidRows();
  return { id: row.id, provider: row.provider, address: row.address.trim(), zipCode: row.zipCode, status: "eligible",
    referenceId: row.referenceId, checkedAt: row.checkedAt,
    ...(row.sourceUrl !== undefined ? { sourceUrl: row.sourceUrl } : {}), ...(row.resultUrl !== undefined ? { resultUrl: row.resultUrl } : {}) };
};

const orderRows = (left, right) => left.zipCode.localeCompare(right.zipCode)
  || physicalKey(left).localeCompare(physicalKey(right)) || left.provider.localeCompare(right.provider) || left.id.localeCompare(right.id);
const latestRows = rows => {
  const byAddress = new Map();
  for (const row of rows) {
    const key = `${row.provider}:${physicalKey(row)}`;
    const previous = byAddress.get(key);
    if (!previous || row.checkedAt > previous.checkedAt || (row.checkedAt === previous.checkedAt && row.id.localeCompare(previous.id) < 0)) byAddress.set(key, row);
  }
  return [...byAddress.values()].sort(orderRows);
};
const sanitizeRows = rows => {
  if (!Array.isArray(rows) || rows.length > MAX_ROWS) throw invalidRows();
  return latestRows(rows.map(sanitizeRow));
};

export const getVerifiedAddressKey = row => physicalKey(sanitizeRow(row));

const normalizeNtid = ntid => {
  if (typeof ntid !== "string" || !/^[a-z0-9]{3,32}$/.test(ntid.trim().toLowerCase())) throw invalidNtid();
  return ntid.trim().toLowerCase();
};
const sanitizeUsage = usage => {
  if (!record(usage) || typeof usage.ntid !== "string"
    || (usage.ntid !== "" && normalizeNtid(usage.ntid) !== usage.ntid)
    || (usage.usedAt === null ? usage.ntid !== "" : !validTime(usage.usedAt))) throw corrupt();
  return { address: sanitizeRow(usage.address), ntid: usage.ntid, usedAt: usage.usedAt };
};
const completeUsageHistory = (usedIds, addresses, lastClaimedAddress, history) => {
  const known = new Map();
  for (const address of addresses) {
    const key = physicalKey(address);
    const previous = known.get(key);
    if (!previous || address.checkedAt > previous.checkedAt
      || (address.checkedAt === previous.checkedAt && orderRows(address, previous) < 0)) known.set(key, address);
  }
  // The last claim is the one exact issuance snapshot available in old state.
  if (lastClaimedAddress) known.set(physicalKey(lastClaimedAddress), lastClaimedAddress);
  const result = history.map(usage => ({ ...usage, address: { ...usage.address } }));
  const recorded = new Set(result.map(usage => physicalKey(usage.address)));
  for (const key of usedIds) {
    if (!recorded.has(key) && known.has(key)) {
      result.push({ address: { ...known.get(key) }, ntid: "", usedAt: null });
      recorded.add(key);
    }
  }
  return result;
};

const emptyState = userId => ({ version: VERSION, userId, addresses: [], usedIds: [], usedAddresses: [], lastClaimedAddress: null });
const readState = (userId, storage) => {
  let raw;
  try { raw = storage.getItem(ownerKey(userId)); } catch (error) { throw storageFailure(error); }
  if (raw === null || raw === undefined) return emptyState(userId);
  try {
    const state = JSON.parse(raw);
    if (!record(state) || state.version !== VERSION || state.userId !== userId || !Array.isArray(state.addresses)
      || !Array.isArray(state.usedIds) || state.usedIds.length > 100000 || new Set(state.usedIds).size !== state.usedIds.length
      || state.usedIds.some(value => typeof value !== "string" || !/^\d{5}:[A-Z0-9/ ]+$/.test(value) || value.length > 300)
      || (state.lastClaimedAddress !== null && !record(state.lastClaimedAddress))) throw corrupt();
    const addresses = sanitizeRows(state.addresses);
    if (addresses.length !== state.addresses.length) throw corrupt();
    const lastClaimedAddress = state.lastClaimedAddress === null ? null : sanitizeRow(state.lastClaimedAddress);
    if (lastClaimedAddress && !state.usedIds.includes(physicalKey(lastClaimedAddress))) throw corrupt();
    if (state.usedAddresses !== undefined && (!Array.isArray(state.usedAddresses) || state.usedAddresses.length > 100000)) throw corrupt();
    const history = (state.usedAddresses || []).map(sanitizeUsage);
    const historyKeys = history.map(usage => physicalKey(usage.address));
    const usedIds = new Set(state.usedIds);
    if (new Set(historyKeys).size !== historyKeys.length || historyKeys.some(key => !usedIds.has(key))) throw corrupt();
    // Older browsers stored consumption keys only. Preserve every key, including
    // those no longer in the catalog, and leave unknown identity/time empty.
    const usedAddresses = completeUsageHistory(state.usedIds, addresses, lastClaimedAddress, history);
    return { version: VERSION, userId, addresses, usedIds: [...state.usedIds], usedAddresses, lastClaimedAddress };
  } catch (error) { throw corrupt(error); }
};

const writeState = (userId, storage, state) => {
  try { storage.setItem(ownerKey(userId), JSON.stringify(state)); } catch (error) { throw storageFailure(error); }
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function" && typeof CustomEvent === "function") {
    window.dispatchEvent(new CustomEvent(VERIFIED_ADDRESS_STORAGE_EVENT, { detail: { userId } }));
  }
};
const filterZip = zipCode => {
  if (zipCode === undefined || zipCode === null || zipCode === "" || zipCode === "all") return "";
  if (typeof zipCode !== "string" || !/^\d{5}$/.test(zipCode)) throw new VerifiedAddressStorageError("LOCAL_ZIP_INVALID", "Choose a valid ZIP code.");
  return zipCode;
};

const progress = (state, zipCode) => {
  const byPhysicalAddress = new Map();
  for (const row of state.addresses) {
    if (zipCode && row.zipCode !== zipCode) continue;
    const key = physicalKey(row);
    const previous = byPhysicalAddress.get(key);
    if (!previous || row.checkedAt > previous.checkedAt || (row.checkedAt === previous.checkedAt && orderRows(row, previous) < 0)) byPhysicalAddress.set(key, row);
  }
  const addresses = [...byPhysicalAddress.values()].sort(orderRows);
  const used = new Set(state.usedIds);
  return { addresses, usedIds: [...state.usedIds], total: addresses.length,
    remaining: addresses.filter(row => !used.has(physicalKey(row))).length,
    lastClaimedAddress: state.lastClaimedAddress ? { ...state.lastClaimedAddress } : null };
};

export const readSavedVerifiedAddresses = (userId, { storage: supplied } = {}) => {
  ownerKey(userId);
  return readState(userId, getStorage(supplied)).addresses;
};

export const saveVerifiedAddresses = (userId, rows, { replace = false, storage: supplied } = {}) => {
  ownerKey(userId);
  const incoming = sanitizeRows(rows);
  const storage = getStorage(supplied);
  const previous = readState(userId, storage);
  const addresses = latestRows(replace ? incoming : [...previous.addresses, ...incoming]);
  if (addresses.length > MAX_ROWS) throw invalidRows();
  const usedAddresses = completeUsageHistory(previous.usedIds, addresses, previous.lastClaimedAddress, previous.usedAddresses);
  writeState(userId, storage, { ...previous, addresses, usedAddresses });
  return addresses;
};

export const getSavedAddressProgress = (userId, { zipCode = "", storage: supplied } = {}) => {
  ownerKey(userId);
  return progress(readState(userId, getStorage(supplied)), filterZip(zipCode));
};

export const readUsedVerifiedAddresses = (userId, { zipCode = "", ntid, storage: supplied } = {}) => {
  ownerKey(userId);
  const zip = filterZip(zipCode);
  const identity = ntid === undefined || ntid === null || (typeof ntid === "string" && !ntid.trim()) ? "" : normalizeNtid(ntid);
  return readState(userId, getStorage(supplied)).usedAddresses
    .filter(usage => (!zip || usage.address.zipCode === zip) && (!identity || usage.ntid === identity))
    .map(usage => ({ address: { ...usage.address }, ntid: usage.ntid, usedAt: usage.usedAt }));
};

export const takeNextVerifiedAddress = (userId, { zipCode = "", ntid, storage: supplied } = {}) => {
  ownerKey(userId);
  const identity = ntid === undefined ? "" : normalizeNtid(ntid);
  const storage = getStorage(supplied);
  const state = readState(userId, storage);
  const current = progress(state, filterZip(zipCode));
  const used = new Set(state.usedIds);
  const address = current.addresses.find(row => !used.has(physicalKey(row)));
  if (!address) return null;
  const usage = { address: { ...address }, ntid: identity, usedAt: new Date().toISOString() };
  const next = { ...state, usedIds: [...state.usedIds, physicalKey(address)], usedAddresses: [...state.usedAddresses, usage], lastClaimedAddress: { ...address } };
  // localStorage writes are synchronous within this tab. This is a local queue,
  // not a global reservation service across devices or simultaneous tabs.
  writeState(userId, storage, next);
  return { ...usage, address: { ...address }, remaining: current.remaining - 1, total: current.total };
};
